import React from 'react';
import { formCardClass } from '../../Util/formHeightUtils';
import {
  SkeletonAmountTiles,
  SkeletonBlock,
  SkeletonCta,
  SkeletonField,
  SkeletonLabel,
  SkeletonLine,
  SkeletonOptionTile,
  SkeletonOptionTiles,
  SkeletonSegmented,
  SkeletonSummaryRow,
  SkeletonTitleStrip,
} from './SkeletonPieces';

/**
 * The form card's loading frame, one per form type (owner call 2026-10-23).
 *
 * Each body mirrors that type's real FIRST step, section for section, inside
 * the same card markup FormStartPageShell renders (the same `.page`, spacing
 * wrapper, `.frosted-background.cs-form-card`, `.cs-form-title-strip` and
 * `.cs-form-card-body.cs-form-sections` classes). The card, strip and body
 * geometry therefore come from the stylesheet the real form reads, and only
 * the contents are drawn here.
 *
 * `config` is the form's configuration when it is already known (a dashboard
 * preview, or a response that landed while the form type's chunk loads). It
 * lets the frame show the right number of levels or groups and leave out a
 * Fund select the form will not render. Without it the frame draws the common
 * shape.
 */

/** Which skeleton a form type gets. Legacy and unknown types take the standard one. */
export function skeletonKindFor(formType) {
  switch (formType) {
    case 'CROWDFUNDING_FORM':
      return 'levels';
    case 'PRODUCT':
      return 'sponsorship';
    case 'EVENT':
      return 'event';
    default:
      return 'standard';
  }
}

const MAX_TILES = 4;

// Levels and groups are configured per frequency (`oneTimeCrowdfundingOptions`,
// `monthlyProductGroups`…); the form opens on its default frequency's list.
const FREQUENCY_PREFIX = { ONE_TIME: 'oneTime', DAILY: 'daily', WEEKLY: 'weekly', MONTHLY: 'monthly', ANNUALLY: 'annually' };
const openingList = (config, key) => {
  const list = config?.[`${FREQUENCY_PREFIX[config?.defaultFrequency] || 'oneTime'}${key}`];
  return Array.isArray(list) ? list.slice(0, MAX_TILES) : null;
};
const clampCount = (list, fallback) =>
  Array.isArray(list) && list.length > 0 ? Math.min(list.length, MAX_TILES) : fallback;

// The Once / Repeat track shows when the form offers more than one frequency.
const showsFrequency = (config) => !config || !Array.isArray(config.frequencies) || config.frequencies.length > 1;
// The Fund select shows only for more than one fund (every form type).
const showsFund = (config) => !config || (Array.isArray(config.funds) && config.funds.length > 1);

function StandardBody({ config }) {
  return (
    <>
      {showsFrequency(config) && <SkeletonSegmented />}
      <div className="flex flex-col gap-[var(--cs-space-3)]">
        <div>
          <SkeletonLabel className="w-14" />
          <SkeletonAmountTiles />
        </div>
        <SkeletonField topGap />
      </div>
      {showsFund(config) && <SkeletonField label />}
      <SkeletonCta />
    </>
  );
}

function LevelsBody({ config }) {
  const options = openingList(config, 'CrowdfundingOptions');
  return (
    <>
      {showsFrequency(config) && <SkeletonSegmented />}
      <div className="flex flex-col gap-[var(--cs-space-3)]">
        <div className="pb-[var(--cs-space-1)]">
          <SkeletonLabel className="w-14" />
          {options && options.length > 0 ? (
            <div className="flex w-full flex-col gap-[var(--cs-space-2)]">
              {options.map((option, i) => (
                <SkeletonOptionTile key={i} withDetail={Boolean(option?.description)} />
              ))}
            </div>
          ) : (
            <SkeletonOptionTiles count={3} />
          )}
        </div>
        <SkeletonField />
      </div>
      {showsFund(config) && <SkeletonField label topPad />}
      <SkeletonCta />
    </>
  );
}

function SponsorshipBody({ config }) {
  const groups = openingList(config, 'ProductGroups');
  return (
    <>
      {showsFrequency(config) && <SkeletonSegmented />}
      <div className="flex flex-col cs-form-sections" aria-hidden="true">
        <div className="flex w-full flex-col">
          <SkeletonLabel className="w-40" />
          {groups && groups.length > 0 ? (
            <div className="flex w-full flex-col gap-[var(--cs-space-2)]">
              {groups.map((group, i) => (
                <SkeletonOptionTile key={i} trailing={false} withDetail={Boolean(group?.productGroupDescription)} />
              ))}
            </div>
          ) : (
            <SkeletonOptionTiles count={2} trailing={false} withDetail={false} />
          )}
        </div>
        {/* The item box: the options track flush at its top, then the price row. */}
        <div className="flex w-full flex-col">
          <SkeletonLabel className="w-36" />
          <div className="overflow-hidden rounded-[var(--cs-control-radius)] border border-[color:var(--cs-border-control)] bg-[color:var(--cs-surface-0)]">
            <SkeletonBlock shape="control" className="h-[var(--cs-size-segment-track)] w-full" />
            <div className="box-border flex w-full items-center justify-between gap-[var(--cs-space-2)] border-t border-[color:var(--cs-border-control)] px-[var(--cs-space-4)] py-[var(--cs-space-3)]">
              <SkeletonLine leading="var(--cs-leading-28)" className="w-20" />
              <SkeletonBlock shape="pill" className="h-[var(--cs-size-stepper-touch)] w-20 sm:h-[var(--cs-size-stepper)]" />
            </div>
          </div>
        </div>
      </div>
      {showsFund(config) && <SkeletonField label topPad />}
      <SkeletonSummaryRow />
      <SkeletonCta />
    </>
  );
}

function EventBody({ config }) {
  const tickets = Array.isArray(config?.tickets) ? config.tickets : null;
  const count = clampCount(tickets, 3);
  return (
    <>
      <SkeletonOptionTiles count={count} stepper />
      <SkeletonSummaryRow />
      <SkeletonCta />
    </>
  );
}

const BODY = {
  standard: StandardBody,
  levels: LevelsBody,
  sponsorship: SponsorshipBody,
  event: EventBody,
};

/** The body for one form type, without the card around it. */
export function FormSkeletonBody({ formType, config }) {
  const Body = BODY[skeletonKindFor(formType)];
  return <Body config={config} />;
}

/**
 * The card around a body, in each of the form's display modes, with the class
 * names FormStartPageShell uses for that mode:
 *   - dynamic: the embedded card, 24 above and below, 12 in from the iframe edge;
 *   - hosted:  the hosted page's rail card;
 *   - overlay: the overlay's form pane, which draws its own edge;
 *   - drawer:  the mobile drawer, which has its own strip, so none here;
 *   - rail:    the card alone, full width, for a caller that owns the layout
 *              around it (the hosted page's loading screen puts it in its rail).
 */
export function FormSkeletonCard({ formType, config, mode = 'dynamic', className = '', statusProps = {} }) {
  const isDrawer = mode === 'drawer';
  const isOverlay = mode === 'overlay';
  const isHosted = mode === 'hosted';

  const pageClassName = [
    'page active h-full items-center iframe-content-container page-transition-container',
    !isDrawer && !isHosted && !isOverlay ? 'container' : '',
    !isDrawer && !isOverlay ? 'mx-auto' : '',
    isDrawer ? 'w-full px-1' : isHosted ? 'max-w-lg px-3' : isOverlay ? 'w-full flex-1 flex flex-col' : 'max-w-md',
    className,
  ].filter(Boolean).join(' ');

  const wrapperClassName = isHosted
    ? 'hosted-spacing-wrapper'
    : isOverlay
      ? 'flex-1 flex flex-col'
      : isDrawer
        ? 'h-full'
        : 'dynamic-spacing-wrapper';
  const wrapperStyle = {
    ...(isHosted ? {} : { padding: isDrawer ? '16px 0' : isOverlay ? '0' : '24px 0' }),
    margin: '0',
    boxSizing: 'border-box',
  };

  // The same card recipe and page floor as the real form (formCardClass), so the swap from
  // skeleton to form does not change the card's height.
  const isEvent = skeletonKindFor(formType) === 'event';
  const cardClassName = isDrawer
    ? 'flex h-full flex-col'
    : `${formCardClass({ isHostedPage: isHosted, isOverlay, isEvent })} flex flex-col`;

  const strip = !isDrawer && <SkeletonTitleStrip titleWidth={skeletonKindFor(formType) === 'event' ? 'w-24' : 'w-28'} />;
  const body = (
    <div
      className={`${isDrawer ? '' : 'cs-form-card-body'} flex flex-1 flex-col`}
      style={{ minHeight: 0, ...(isDrawer ? { padding: 0 } : {}) }}
    >
      <div className="flex flex-1 flex-col cs-form-sections" style={{ minHeight: 0 }} aria-hidden="true">
        <FormSkeletonBody formType={formType} config={config} />
      </div>
    </div>
  );

  if (mode === 'rail') {
    return (
      <div className={`${formCardClass({ isHostedPage: true, isEvent: skeletonKindFor(formType) === 'event' })} flex w-full flex-col ${className}`.trim()} {...statusProps}>
        {statusProps.role && <span className="sr-only">{statusProps['aria-label']}, please wait...</span>}
        {strip}
        {body}
      </div>
    );
  }

  return (
    <div className={pageClassName} {...statusProps}>
      {statusProps.role && <span className="sr-only">{statusProps['aria-label']}, please wait...</span>}
      <div className={wrapperClassName} style={wrapperStyle}>
        <div
          className={cardClassName}
          style={isDrawer ? { padding: '10px' } : undefined}
        >
          {strip}
          {body}
        </div>
      </div>
    </div>
  );
}

export default FormSkeletonCard;
